import React, { FC } from "react";
import { Box, Typography } from "@mui/material";
import Badge from "../../../../assets/hamaspik/Badge";
import { PostIfEmploymentHistoryDto, PostIfPositionRulesDto } from "../../../../types/pifDto";
import { getPifRateVerdict, isBound, isCommunityDsp } from "../pifValidation";
import { PifFlow, PifFormValues } from "../types";
import "./PifOfferRail.scss";

// Lives here rather than the workspace header, which is a single pinned row now.
const FLOW_NOTE: Record<PifFlow, string> = {
  default: "",
  new: "New to Hamaspik",
  active: "Currently employed · adding or changing a position",
  inactive: "Rehire · previously employed",
};

interface PifOfferRailProps {
  values: PifFormValues;
  flow: PifFlow;
  rules: PostIfPositionRulesDto | null;
  /** From GET employment-history — empty for a brand-new applicant. */
  history: PostIfEmploymentHistoryDto[];
}

/** "Apr 2022 — present" from the API's two dates. */
const formatSpan = (startDate?: string | null, endDate?: string | null): string => {
  const label = (value?: string | null) => {
    if (!value) return "";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? ""
      : parsed.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };
  const from = label(startDate);
  const to = endDate ? label(endDate) : "present";
  return from ? `${from} - ${to}` : to;
};

const PifOfferRail: FC<PifOfferRailProps> = ({ values: f, flow, rules, history }) => {
  const verdict = getPifRateVerdict(f.rate, rules);
  const hasBand = isBound(rules?.positionMin) && isBound(rules?.positionMax);

  const rows: { k: string; v: string }[] = [
    { k: "Schedule", v: f.empType + (f.hours ? ` · ${f.hours} hrs` : "") },
    { k: "Starts", v: f.startDate || "-" },
    { k: "Reports to", v: f.reportsTo || "-" },
    { k: "Kind of hire", v: f.hireType || "-" },
  ];
  if (f.client) rows.push({ k: "Client", v: f.client });

  const why: string[] = [];
  if (rules) {
    why.push(
      rules.isOffice
        ? "Office-based position."
        : "Field-based position.",
    );
    why.push(
      isCommunityDsp(rules)
        ? "Community-based DSP: always part-time, and a service plus client assignment is required."
        : !rules.isScheduleBased
          ? "Not a schedule-based position, so full-time is locked off."
          : "Schedule-based - part-time or full-time both allowed.",
    );
    why.push(
      rules.mayOperateVehicle
        ? "This position may drive an agency vehicle, so the licence questions apply."
        : "No agency driving in this position - the licence questions are hidden.",
    );
  } else {
    why.push("Pick a department and position - the rest of the form shapes itself around them.");
  }
  if (flow === "active") why.push("Active employee: personal and contact details are read-only, and the SSN is never shown.");
  if (flow === "inactive") why.push("Rehire: address and contact are editable again - people move. SSN stays masked.");
  if (verdict.blocked) why.push("Rate is outside the hiring band, so this can't be submitted until it changes.");
  else if (verdict.warn) why.push("Rate sits outside the position band, so HR and the Director will look closely at it.");

  const rateClass = !verdict.hasRate || !rules
    ? "pif-offer-rail__rate"
    : `pif-offer-rail__rate pif-offer-rail__rate--${verdict.blocked || verdict.warn ? "warn" : "ok"}`;

  return (
    <Box className="pif-offer-rail">
      <Box className="pif-offer-rail__card">
        <Box className="pif-offer-rail__card-header">
          <Typography variant="caption" className="pif-offer-rail__eyebrow">
            THE OFFER
          </Typography>
          <Typography variant="caption" className="pif-offer-rail__eyebrow pif-offer-rail__eyebrow--faint">
            DRAFT
          </Typography>
        </Box>
        <Box className="pif-offer-rail__body">
          {FLOW_NOTE[flow] && (
            <Typography variant="caption" className="pif-offer-rail__flow-note">
              {FLOW_NOTE[flow]}
            </Typography>
          )}
          <Box>
            <Typography className="pif-offer-rail__pos-title">{f.pos || "Position not chosen"}</Typography>
            <Typography variant="body2" color="text.secondary">
              {f.dept || "-"}
            </Typography>
          </Box>
          <Box>
            <Typography className={rateClass}>{f.rate ? `$${f.rate}` : "$ -"}</Typography>
            <Typography variant="caption" color="text.secondary">
              {(f.rateType || "rate type not chosen").toLowerCase()}
              {hasBand ? ` · band $${rules?.positionMin}–$${rules?.positionMax}` : ""}
            </Typography>
          </Box>
          <Box className="pif-offer-rail__divider" />
          {rows.map((r) => (
            <Box key={r.k} className="pif-offer-rail__row">
              <Typography variant="caption" color="text.secondary">
                {r.k}
              </Typography>
              <Typography variant="caption" fontWeight={700} color={r.v === "-" ? "text.secondary" : "text.primary"}>
                {r.v}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {history.length > 0 && (
        <Box className="pif-offer-rail__card">
          <Box className="pif-offer-rail__card-header-muted">
            <Typography variant="caption" className="pif-offer-rail__eyebrow pif-offer-rail__eyebrow--subtle">
              ALREADY AT HAMASPIK
            </Typography>
          </Box>
          <Box className="pif-offer-rail__body-pad pif-offer-rail__history-list">
            {history.map((p, index) => (
              <Box
                key={`${p.title ?? "position"}-${p.startDate ?? index}`}
                className={`pif-offer-rail__history-item${p.isActive ? " pif-offer-rail__history-item--active" : ""}`}
              >
                <Box className="pif-offer-rail__history-top">
                  <Typography variant="body2" fontWeight={700}>
                    {p.title || "-"}
                  </Typography>
                  <Badge variant={p.isActive ? "success" : "neutral"} size="sm">
                    {p.isActive ? "ACTIVE" : "ENDED"}
                  </Badge>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {[p.department, p.employmentType].filter(Boolean).join(" · ") || "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatSpan(p.startDate, p.endDate)}
                  {p.supervisorName ? ` · ${p.supervisorName}` : ""}
                  {p.supervisorExt ? ` ext. ${p.supervisorExt}` : ""}
                </Typography>
              </Box>
            ))}
            <Typography variant="caption" color="text.secondary">
              Faded rows are past positions. Call the supervisor before you move one of their people.
            </Typography>
          </Box>
        </Box>
      )}

      <Box className="pif-offer-rail__card">
        <Box className="pif-offer-rail__card-header-muted">
          <Typography variant="caption" className="pif-offer-rail__eyebrow pif-offer-rail__eyebrow--subtle">
            WHY THE FORM LOOKS LIKE THIS
          </Typography>
        </Box>
        <Box className="pif-offer-rail__body-pad">
          {why.map((r) => (
            <Box key={r} className="pif-offer-rail__rule">
              <Box className="pif-offer-rail__rule-dot" />
              <Typography variant="caption" color="text.secondary">
                {r}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default PifOfferRail;
